import { provideHttpClient, withInterceptors } from '@angular/common/http';
import {
  ApplicationConfig,
  ErrorHandler,
  inject,
  provideAppInitializer,
  provideZoneChangeDetection,
} from '@angular/core';
import {
  provideRouter,
  Router,
  withHashLocation,
  withInMemoryScrolling,
  withViewTransitions,
} from '@angular/router';
import {
  CcdlSpinnerService,
  SpinnerOptions,
  SpinnerType,
} from '@cpm-pr1618001/ng-design-lib';
import {
  AppConfig,
  AppInitializerEffects,
  AppInitializerFacade,
  appInitializerFeature,
  CpmGlobalErrorHandler,
  DashboardLabelsEffects,
  dashboardLabelsFeature,
  HttpInterceptorsFn,
  HttpRequestStatus,
  XsrfInterceptorFn,
} from '@cpm-pr1618003/ng-initializer-lib';
import { provideEffects } from '@ngrx/effects';
import { provideState, provideStore } from '@ngrx/store';
import { provideStoreDevtools } from '@ngrx/store-devtools';
import {
  catchError,
  combineLatest,
  filter,
  finalize,
  firstValueFrom,
  map,
  of,
  take,
} from 'rxjs';
import { environment } from 'src/environments/environment';
import { routes } from './app.routes';
import { LandingPageEffects } from './landing-page/store/effects/landing-page.effects';
import { landingPageFeature } from './landing-page/store/feature/landing-page.feature';
import { InitEffects } from './store/effects/init.effects';
import { initFeature } from './store/feature/init.feature';
import {
  getTrustedCaptrixonOrigins,
  redirectToTrustedUrl,
} from './utils/redirect.utils';

export const initializeApp = (): any => {
  const router = inject(Router);
  const facade = inject<AppInitializerFacade>(AppInitializerFacade);
  const ccdlSpinnerService = inject(CcdlSpinnerService);
  const spinnerOptions: SpinnerOptions = {
    type: SpinnerType.DUAL_RING,
    spinnerText: 'Application initializing...',
  };
  // Open before dispatching the first HTTP request so a fast response cannot race the spinner.
  ccdlSpinnerService.open(spinnerOptions);

  const appConfig: AppConfig = {
    excludeUrls: [
      '/auth/app-initializer',
      '/auth/refresh-token',
      '/auth/silent-refresh-token',
    ],
    bypassExcludeUrl: false,
    baseUrl: environment.baseUrl,
    redirectUrl: environment.cuiAppUrl,
    appInitRequest: {
      appCode: '134002',
      appName: 'capvizon-ui',
    },
    shouldHandle401Errors: false,
  };
  facade.setAppConfig(appConfig);

  facade.loadAppInitializer();
  facade.loadRefreshToken();

  const appPromise$: Promise<boolean> = firstValueFrom(
    combineLatest([facade.appResponse$, facade.getRefreshTokenData$]).pipe(
      filter(
        ([appResponse, refreshToken]) =>
          (appResponse?.appInitializerRequestStatus ===
            HttpRequestStatus.Success ||
            appResponse?.appInitializerRequestStatus ===
              HttpRequestStatus.Failed) &&
          (refreshToken?.refreshTokenRequestStatus ===
            HttpRequestStatus.Success ||
            refreshToken?.refreshTokenRequestStatus ===
              HttpRequestStatus.Failed),
      ),
      take(1),
      map(([appResponse, refreshToken]) => {
        if (
          appResponse.appInitializerRequestStatus ===
            HttpRequestStatus.Failed ||
          !appResponse.isAppValid
        ) {
          router.navigate(['/error-page']);
          return false;
        }

        const hash = window.location.hash;
        const isPublicRoute =
          hash.includes('/legal/') ||
          hash.includes('/LandingPage/AboutUs') ||
          hash.includes('/reactivate-account') ||
          hash.includes('/activating-account');
        if (
          refreshToken.refreshTokenRequestStatus ===
            HttpRequestStatus.Success &&
          refreshToken.userId &&
          refreshToken.isRefreshTokenValid &&
          !isPublicRoute
        ) {
          const didRedirect = redirectToTrustedUrl(
            environment.captrixonURL,
            getTrustedCaptrixonOrigins(environment.captrixonURL),
          );
          if (!didRedirect) {
            router.navigate(['/error-page']);
          }
        }
        return true;
      }),
      catchError(() => of(false)),
      finalize(() => ccdlSpinnerService.close()),
    ),
  );
  return () => appPromise$;
};

export const appConfig: ApplicationConfig = {
  providers: [
    provideAppInitializer(initializeApp),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      routes,
      withHashLocation(),
      withViewTransitions(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideHttpClient(
      withInterceptors([XsrfInterceptorFn, HttpInterceptorsFn]),
    ),
    provideStore(),
    provideState(initFeature),
    provideEffects([
      InitEffects,
      LandingPageEffects,
      AppInitializerEffects,
      DashboardLabelsEffects,
    ]),
    provideState(landingPageFeature),
    provideState(appInitializerFeature),
    provideState(dashboardLabelsFeature),
    ...(environment.showDevTools
      ? [provideStoreDevtools({ maxAge: 25, logOnly: true })]
      : []),
    {
      provide: ErrorHandler,
      useClass: CpmGlobalErrorHandler,
    },
  ],
};
